/* eslint-disable no-use-before-define */

import Vue from 'vue';
import Router from 'vue-router';
import store from './store';
import Login from './views/Login.vue';
import RegisterUser from './components/AppView/RegisterUser.vue';
import AppView from './views/AppView.vue';
import ByblioHome from './components/AppView/Home.vue';
import AccountManagement from '@/views/AccountManagement.vue';
import CollectionManagement from './components/AppView/Settings/CollectionManagement.vue';
import HowToUse from './components/AppView/HowToUse.vue';
import CollectionContentContainer from './components/AppView/CollectionView/CollectionContentContainer.vue';
import ItemViewPlaceholder from '@/components/AppView/ItemView/ItemViewPlaceholder.vue';
// import { authGuard } from './auth/authGuard';

Vue.use(Router);

const appChecks = (to, from, next) => {
  // If not authorized yet, go to login page
  if (to.name !== 'Login' && !store.state.users.authStatus) {
    next({ path: '/login' });
    return;
  }

  // If the data has been loaded, this means that 1) we have a verified user, and 2) the data is loaded (duh); we can continue to the requested (or a redirected) route
  if (store.state.appState.initialDataLoaded) {
    if (to.name === 'AppView') next({ name: 'Home' });
    else next();
    return;
  }

  // Data has not been loaded yet. Load data (or go to verification process if applicable)
  let verificationStatus;
  store.dispatch('users/getDataAtSetup')
    .then(async (data) => {
      // ({ verificationStatus } = data);
      // if (!verificationStatus) next({ path: '/finishRegistration' });
      // else next();
      next();
    })
    .catch((err) => {
      // If we have a user who is not verified (need to check for false literally, for a verificationStatus of null does not say much about the user being verified or no)
      // (this is just a fallback, in principle unverified users should be detected in the then() clause)
      if (verificationStatus === false) next({ path: '/finishRegistration' });

      // TO DO: fix error handling here!
      // else store.commit('appState/SetScreenOverlayStatus', { status: 'fatalError' });
      else next();
    });
};

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      component: AppView,
      // beforeEnter: authGuard,
      children: [
        {
          path: '',
          name: 'AppView',
          beforeEnter: appChecks,
        },
        {
          path: 'home',
          name: 'Home',
          component: ByblioHome,
          beforeEnter: appChecks,
        },
        {
          path: 'collections',
          name: 'CollectionManagement',
          component: CollectionManagement,
          beforeEnter: appChecks,
        },
        {
          path: 'account',
          name: 'AccountManagement',
          component: AccountManagement,
          beforeEnter: appChecks,
        },
        {
          path: 'howto',
          name: 'HowToUse',
          component: HowToUse,
          beforeEnter: appChecks,
        },
        {
          path: 'collection/:collectionId?',
          name: 'Collection',
          component: CollectionContentContainer,
          beforeEnter: appChecks,
          children: [
            {
              path: '/keyword/*',
              name: 'KeywordList',
              beforeEnter: appChecks,
            },
            {
              path: '/stars/*',
              name: 'StarList',
              beforeEnter: appChecks,
            },
            {
              path: '/card/:cardId',
              name: 'Card',
              beforeEnter: appChecks,
            },
            {
              path: '/:relatedCollectionId/card/:cardId',
              redirect: to => `/card/${to.params.cardId}`,
            },
            {
              path: '/note/:noteId',
              name: 'Note',
              beforeEnter: appChecks,
            },
            {
              path: '/:relatedCollectionId/note/:noteId',
              redirect: to => `/note/${to.params.noteId}`,
            },
          ],
        },
        {
          path: 'item/:cardId',
          name: 'WebViewer',
          component: ItemViewPlaceholder,
          beforeEnter: appChecks,
        },
        {
          path: '/:relatedCollectionId/item/:cardId',
          redirect: to => `/item/${to.params.cardId}`,
        },
      ],
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
    },
    {
      path: '/finishRegistration',
      name: 'FinishRegistration',
      component: RegisterUser,
      // beforeEnter: authGuard,
    },
    {
      path: '/nointernet',
      name: 'NoInternet',
    },
    {
      path: '*', // Catch-all for non-existing routes
      beforeEnter: (to, from, next) => {
        next({ path: '/' });
      },
    },
  ],
});

const setupAppState = ({ path, name: destination, params, redirectedFrom }) => {
  // The main thing we need to do here is to select the collection where we're going to.
  // This doubles as validation: if we do not have a collectionId in the end, it was not a valid request (i.e. resource does not exist).

  // Some destinations need no such setup (to do: do keywordList/StarList actually need some validation?)
  const destinationsThatNeedNoSetup = ['AppView', 'FinishRegistration', 'CollectionManagement', 'AccountManagement', 'Home', 'HowToUse', 'KeywordList', 'StarList'];
  if (destinationsThatNeedNoSetup.includes(destination)) return true;

  let collectionId = null;
  // Navigation to collection
  if (destination === 'Collection') {
    collectionId = Number(params.collectionId);
    if (!collectionId) return false; // If none is provided, just to to home, do not show a message

    const collectionExists = store.getters['collections/allCollectionIds'].includes(collectionId);
    if (!collectionExists) collectionId = null;
    else store.dispatch('collections/selectCollection', { collectionId });
  }

  // Internal navigation to card, note, or item
  else if (redirectedFrom) collectionId = Number(redirectedFrom.split('/')[1]);

  // Adding a new card via extension (requires query, so cannot use regular internal navigation above)
  else if (Number(params.cardId) === 0) return true;

  // External navigation to card, note, or item (i.e. direct url navigation to a note, card, or item, or using browser back buttons)
  else {
    let requestedItem = null;
    if (path.includes('card/') || path.includes('item/')) requestedItem = store.getters['cards/getCardRef']({ cardId: Number(params.cardId) });
    else if (path.includes('note/')) requestedItem = store.getters['notes/getNoteRef']({ noteId: Number(params.noteId) });

    if (requestedItem) ({ relatedCollectionId: collectionId } = requestedItem);

    // TO DO: try to check if this comes from browser history navigation or if it's at startup (checking whether there is router history or so).
    // If it is browser history navigation, try to go further back or further forwards, so that when you deleted a collection you can go back to the view
    // in the history before the history entry of that deleted collection (or card or whatever)
  }

  // Invalid request, abort
  if (!collectionId) {
    let requestType = destination === 'WebViewer' ? 'file' : destination.toLowerCase();
    if (requestType === 'cards' || requestType === 'notes') requestType = 'collection';
    store.dispatch('notifications/setNotification', `Cannot find that ${requestType}!`);
    return false;
  }

  return true;
};

router.beforeResolve((to, from, next) => {
  if (to.name === 'Login') {
    next();
    return;
  }

  if (to.name === 'AppView') {
    next({ name: 'Home' });
    return;
  }

  if (to.name === 'NoInternet') {
    next();
    return;
  }

  // Validate request, select relevant stuff in Vuex (if request is valid ofc)
  if (!setupAppState(to)) {
    // Invalid request. If this happened on load, redirect. Else, just cancel.
    if (!store.state.appState.appIsReady) {
      // Go to a collection (should actually select the alphabetically first main collection in absence of selectedCollection, but that's a // to do)
      const collectionId = store.state.collections.selectedCollection || store.state.collections.collectionNodes['0'].children[0].id;
      next({ path: `/collection/${collectionId}` });
    }
    else next(false);
    return;
  }

  // Add to navigation history, except for new cards (else going back adds the card again)
  if (!to.path.endsWith('/card/0') && !to.path.includes('/card/0?')) store.dispatch('appState/addNavigationToHistory', to.path);
  next();
  if (!store.state.appState.appIsReady) store.commit('appState/SetAppToReady');
});

export default router;
